<script setup lang="ts">
import { watch } from 'vue';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import { Gallery } from '@nora-monorepo/ui-shared';

import { useAttachmentRefresh } from '#nora-chat-ui/composables/useAttachmentRefresh';

const props = defineProps<{
  attachment: MessageAttachmentDTO;
  conversationId: string;
  messageId: string;
  canPrevious?: boolean;
  canNext?: boolean;
}>();

const emit = defineEmits<{
  previous: [];
  next: [];
  close: [];
}>();

const { watchAttachment, unwatchAttachment, refreshConversationAttachment } = useAttachmentRefresh();

// Hidden attachments have no rendered preview watching them, and a refresh only works for watched attachments.
watch(
  () => props.attachment.id,
  (attachmentId, _previousId, onCleanup) => {
    watchAttachment({
      attachment: props.attachment,
      conversationId: props.conversationId,
      messageId: props.messageId,
    });

    onCleanup(() => unwatchAttachment({ attachmentId }));
  },
  { immediate: true },
);

/**
 * Requests fresh signed URLs for the shown attachment.
 */
async function refreshUrls() {
  await refreshConversationAttachment({
    conversationId: props.conversationId,
    attachmentId: props.attachment.id,
  });
}
</script>

<template>
  <Gallery
    :file="attachment"
    :file-key="attachment.id"
    :refresh-urls="refreshUrls"
    :can-previous="canPrevious"
    :can-next="canNext"
    @previous="emit('previous')"
    @next="emit('next')"
    @close="emit('close')"
  />
</template>
